"use client";

import { useRouter, useParams } from "next/navigation";
import Cookies from "js-cookie";
import { useEffect, useState } from "react";
import { uuidV4Regex } from "@app/utils";
import Image from "next/image";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@components/ui/DropdownMenu";
import { cn } from "@utils/cn";
type Branch = {
  id: string;
  key: string;
  name: string;
  image: {
    url: string;
  };
};

// Utility functions for tab-specific storage
const TabStorage = {
  // Generate or retrieve a unique tab ID
  getTabId: (): string => {
    if (typeof window === "undefined") return "";
    let tabId = sessionStorage.getItem("tabId");
    if (!tabId) {
      tabId = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem("tabId", tabId);
    }
    return tabId;
  },
  // Get branch for current tab (falls back to cookie if not set)
  getBranchId: (): string | null => {
    if (typeof window === "undefined") return null;
    const tabId = TabStorage.getTabId();
    const tabBranchId = sessionStorage.getItem(`branch_id_${tabId}`);

    // If this tab has a specific branch, use it
    if (tabBranchId) {
      return tabBranchId;
    }

    // Otherwise fall back to cookie (for initial load)
    return Cookies.get("branch_id") || null;
  },
  // Set branch for current tab
  setBranchId: (branchId: string, id: string): void => {
    if (typeof window === "undefined") return;
    const tabId = TabStorage.getTabId();

    // Store in sessionStorage (tab-specific)
    sessionStorage.setItem(`branch_id_${tabId}`, branchId);
    sessionStorage.setItem(`id_${tabId}`, id);

    // Also update cookies for new tabs to inherit this selection
    Cookies.set("branch_id", branchId);
    Cookies.set("id", id);

    // Keep localStorage in sync (optional - for persistence across browser restarts)
    localStorage.setItem("branch_id", branchId);
    localStorage.setItem("id", id);
  },
  // Get id for current tab
  getId: (): string | null => {
    if (typeof window === "undefined") return null;
    const tabId = TabStorage.getTabId();
    const tabBranchId = sessionStorage.getItem(`id_${tabId}`);
    if (tabBranchId) {
      return tabBranchId;
    }
    return Cookies.get("id") || null;
  }
};
export const BranchMenu = ({
  colorPrimary,
  collapsed
}: {
  colorPrimary: string;
  collapsed: boolean;
}) => {
  const router = useRouter();
  const params = useParams();
  const branchKeyFromParams = Array.isArray(params.branch_id) ? params.branch_id[0] : params.branch_id;
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [allBranches, setAllBranches] = useState<Branch[]>([]);

  // Initialize tab on mount
  useEffect(() => {
    TabStorage.getTabId(); // Ensure tab ID is created
  }, []);
  useEffect(() => {
    const encoded = Cookies.get("user_branches");
    if (encoded) {
      try {
        const parsed: Branch[] = JSON.parse(decodeURIComponent(encoded));
        setAllBranches(parsed);

        // Priority: URL params > sessionStorage > cookie
        let currentBranchKey = branchKeyFromParams;
        if (!currentBranchKey) {
          // If no URL param, check if this tab has a stored preference
          currentBranchKey = TabStorage.getBranchId() || "";
        }
        if (currentBranchKey) {
          const current = parsed.find(b => b.key === currentBranchKey);
          if (current) {
            setSelectedBranch(current);
            // Store in tab-specific storage if from URL
            if (branchKeyFromParams) {
              TabStorage.setBranchId(current.key, current.id);
            }
          }
        }
      } catch (err) {
        console.error("Failed to parse branches from cookie", err);
      }
    } else {
      console.info("No user_branches cookie found");
    }
  }, [branchKeyFromParams]);
  const handleBranchChange = (branch: Branch) => {
    TabStorage.setBranchId(branch.key, branch.id);
    setSelectedBranch(branch);
    const pathSegments = window.location.pathname.split("/");
    if (pathSegments.length > 1) {
      pathSegments[1] = branch.key;
    }
    const newPath = pathSegments.join("/") || "/";
    const mapped = {
      academies: "academies/academies-management",
      marketing: "marketing/amenities-management",
      "assets-rental": "assets-rental/assets-rental-management",
      dining: "dining/restaurants",
      "performance-plus": "performance-plus/sports-management"
    };
    const includedUuid = newPath.split("/").some(path => uuidV4Regex.test(path));
    if (includedUuid) {
      const path_0 = newPath.split("/")[2];
      const current_0 = `/${newPath.split("/")[1]}/${mapped[path_0 as keyof typeof mapped]}`;
      router.replace(current_0);
      return;
    }
    router.replace(newPath);
  };
  return <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className={cn("flex h-full min-h-0 w-full cursor-pointer items-center rounded-2xl px-3 text-left outline-none transition-colors hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-white/60", collapsed ? "justify-center" : "justify-start gap-3")} style={{
        backgroundColor: colorPrimary
      }} aria-label="Select branch">
          {selectedBranch?.image?.url && <span className={cn("flex items-center", collapsed ? "w-full" : "w-1/3")}>
              <Image width={72} height={72} src={selectedBranch.image.url || ""} alt="branch image" sizes="72px" className="h-11 w-full rounded-xl object-cover" style={{
            width: "100%",
            height: "100%"
          }} />
            </span>}
          {!collapsed && <span className="min-w-0 flex-1 truncate font-medium text-white">
              {selectedBranch?.name}
            </span>}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" sideOffset={8} className="z-[1000] min-w-48">
        {allBranches.map(branch_0 => {
        const isSelected = branch_0.key === branchKeyFromParams;
        return <DropdownMenuItem key={branch_0.id} onSelect={() => handleBranchChange(branch_0)} className={cn("w-full cursor-pointer", isSelected && "bg-primary text-white focus:bg-primary focus:text-white")}>
              {branch_0.name}
            </DropdownMenuItem>;
      })}
      </DropdownMenuContent>
    </DropdownMenu>;
};